import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FiSend, FiChevronDown, FiChevronUp, FiCheckCircle,
  FiXCircle, FiClock, FiRefreshCw
} from 'react-icons/fi';
import './Campaigns.css';

function statusBadge(status) {
  if (status === 'completed') return <span className="badge badge-success">Completed</span>;
  if (status === 'sending')   return <span className="badge badge-info">Sending…</span>;
  if (status === 'failed')    return <span className="badge badge-error">Failed</span>;
  return <span className="badge badge-default">{status}</span>;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function Campaigns({ addToast }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [logs, setLogs] = useState({});   // { [campaignId]: [] }
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/email-campaigns');
      setCampaigns(res.data.data);
    } catch {
      addToast('Failed to load campaigns', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (logs[id]) return; // already loaded
    try {
      setLogsLoading(true);
      const res = await axios.get(`/api/email-campaigns/${id}/logs`);
      setLogs(prev => ({ ...prev, [id]: res.data.data }));
    } catch {
      addToast('Failed to load email logs', 'error');
    } finally {
      setLogsLoading(false);
    }
  };

  if (loading) {
    return <div className="campaigns-loading"><FiClock size={20} /> Loading campaigns…</div>;
  }

  if (campaigns.length === 0) {
    return (
      <div className="campaigns-empty">
        <FiSend size={40} />
        <h3>No campaigns yet</h3>
        <p>Go to Database, select companies, and click Send Email to start a campaign.</p>
      </div>
    );
  }

  return (
    <div className="campaigns-page">
      <div className="campaigns-header">
        <h2>Campaign History</h2>
        <button className="btn-icon" onClick={fetchCampaigns} title="Refresh">
          <FiRefreshCw size={16} />
        </button>
      </div>

      <div className="campaigns-list">
        {campaigns.map(c => {
          const isOpen = expandedId === c.id;
          const sent = c.emails_sent || 0;
          const failed = c.emails_failed || 0;
          const total = c.total_recipients || 0;
          const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
          const campaignLogs = logs[c.id] || [];

          return (
            <div key={c.id} className={`campaign-card ${isOpen ? 'expanded' : ''}`}>
              <div className="campaign-row" onClick={() => toggleExpand(c.id)}>
                <div className="campaign-col campaign-name-col">
                  <span className="campaign-name">{c.name}</span>
                  <span className="campaign-template">{c.template_name || 'Unknown template'}</span>
                </div>

                <div className="campaign-col campaign-stats-col">
                  <div className="stat-pill sent">
                    <FiCheckCircle size={13} />
                    {sent.toLocaleString()} sent
                  </div>
                  {failed > 0 && (
                    <div className="stat-pill failed">
                      <FiXCircle size={13} />
                      {failed.toLocaleString()} failed
                    </div>
                  )}
                </div>

                <div className="campaign-col campaign-progress-col">
                  <div className="progress-bar-thin">
                    <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="progress-label">{pct}% of {total.toLocaleString()}</span>
                </div>

                <div className="campaign-col campaign-meta-col">
                  {statusBadge(c.status)}
                  <span className="campaign-date">{formatDate(c.started_at)}</span>
                </div>

                <div className="campaign-col campaign-chevron-col">
                  {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                </div>
              </div>

              {isOpen && (
                <div className="campaign-detail">
                  {logsLoading && !logs[c.id] ? (
                    <p className="logs-loading">Loading logs…</p>
                  ) : campaignLogs.length === 0 ? (
                    <p className="logs-empty">No per-recipient logs found.</p>
                  ) : (
                    <table className="logs-table">
                      <thead>
                        <tr>
                          <th>Recipient</th>
                          <th>Email</th>
                          <th>Subject</th>
                          <th>Status</th>
                          <th>Time</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaignLogs.map(log => (
                          <tr key={log.id} className={`log-row log-${log.status}`}>
                            <td>{log.recipient_name || '—'}</td>
                            <td className="log-email">{log.recipient_email}</td>
                            <td className="log-subject">{log.subject}</td>
                            <td>
                              {log.status === 'sent'
                                ? <span className="log-badge sent"><FiCheckCircle size={12} /> Sent</span>
                                : <span className="log-badge failed"><FiXCircle size={12} /> Failed</span>
                              }
                            </td>
                            <td className="log-time">
                              {formatDate(log.sent_at || log.failed_at)}
                            </td>
                            <td className="log-reason">
                              {log.failure_reason || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
